import { useAuth, useClerk, useUser } from "@clerk/nextjs";
import { useQueryClient } from "@tanstack/react-query";
import Switch from "components/Switch";
import {
  ABOUT_URL,
  GPT_URL,
  HELP_CENTER_URL,
  SLACK_URL,
  TWITTER_URL,
} from "constants/config";
import path from "constants/path";
import useIncognitoMode from "features/User/hooks/useIncognitoMode";
import useSubscription from "features/User/hooks/useSubscription";
import { apiManagementPageUrl } from "helpers/pageUrl";
import { SubscriptionPlan } from "enums/subscription-plans";
import useLabels from "hooks/useLabels";
import Link from "next/link";
import IncognitoModeUserPicture from "./IncognitoModeUserPicture";
import {
  ArrowRightFromLine,
  CircleHelp,
  Code,
  Ghost,
  Settings,
  Sun,
  Moon,
} from "lucide-react";
import { useTheme, getSystemTheme } from "design_system/utils/theme";
import { Text } from "design_system";

export const UserMenuContents = () => {
  const { user } = useUser();
  const { signOut } = useClerk();
  const { isSignedIn } = useAuth();
  const { isIncognitoModeActive, toggleIncognitoMode } = useIncognitoMode();
  const [userPopoverLabels] = useLabels(
    "features.sidebar-navigation.user-popover"
  );
  const [sidebarLabels] = useLabels("screens.sidebar");

  const { subscriptionType } = useSubscription();
  const userCanViewApiLink = subscriptionType === SubscriptionPlan.TEAM;

  const [labels] = useLabels("incognito-mode");

  const queryClient = useQueryClient();

  const handleSignOut = () => {
    // Eliminate all cached API data to avoid leaking between users.
    queryClient.clear();

    signOut({ redirectUrl: path.SIGN_IN });
  };

  const { themeMode, resolvedTheme, setTheme, isSystem } = useTheme();

  const handleThemeToggle = () => {
    // If system preference is dark: cycle light -> dark -> system -> light...
    // If system preference is light: cycle dark -> light -> system -> dark...
    const systemIsDark = getSystemTheme() === "dark";

    if (themeMode === "system") {
      // From system, go to the opposite of system preference to start the cycle
      setTheme(systemIsDark ? "light" : "dark");
    } else if (themeMode === "light") {
      // From light, go to dark or system based on system preference
      setTheme(systemIsDark ? "dark" : "system");
    } else if (themeMode === "dark") {
      // From dark, go to system or light based on system preference
      setTheme(systemIsDark ? "system" : "light");
    }
  };

  return (
    <div className="w-64">
      <div className="flex gap-3 items-center p-3 mb-1 border-b border-b-border-base">
        <div className="flex justify-center items-center w-6 h-6 rounded-full">
          {isIncognitoModeActive ? (
            <IncognitoModeUserPicture />
          ) : (
            <img
              src={user?.imageUrl}
              className="w-full h-full rounded-full"
              alt="user profile"
            />
          )}
        </div>
        <div className="overflow-hidden flex-1">
          <span className="block text-fg-base tiny-md">
            {user?.firstName || ""}&nbsp;{user?.lastName || ""}
          </span>
          <span className="block truncate tiny-normal text-fg-muted">
            {user?.primaryEmailAddress?.emailAddress}
          </span>
        </div>
      </div>
      <div className="text-fg-base flex items-center mx-1 rounded-lg hover:bg-bg-faint tiny-md">
        <button
          className="flex gap-3 items-center w-full tiny-md h-9 px-2"
          onClick={handleThemeToggle}
        >
          <div className="flex flex-1 gap-3 items-center">
            {themeMode === "system" ? (
              resolvedTheme === "light" ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )
            ) : themeMode === "light" ? (
              <Sun className="w-5 h-5" />
            ) : (
              <Moon className="w-5 h-5" />
            )}
            <Text size="x-sm" weight="medium">
              {userPopoverLabels["toggle-theme"]}
            </Text>
          </div>
          <Text
            family="mono"
            size="x-sm"
            weight="medium"
            theme="muted"
            className="uppercase"
          >
            {themeMode === "system"
              ? userPopoverLabels["theme-system"]
              : themeMode === "light"
                ? userPopoverLabels["theme-light"]
                : userPopoverLabels["theme-dark"]}
          </Text>
        </button>
      </div>
      <div className="text-fg-base flex justify-between items-center h-9 px-2 mx-1 rounded-lg hover:bg-bg-faint tiny-md">
        <div className="flex gap-3 items-center">
          <Ghost className="w-5 h-5" />
          <span>{labels["incognito-mode"]}</span>
        </div>
        <Switch
          active={isIncognitoModeActive}
          onChange={() => {
            toggleIncognitoMode();
          }}
        />
      </div>
      <Link
        href={HELP_CENTER_URL}
        className="text-fg-base flex gap-3 items-center h-9 px-2 mx-1 rounded-lg hover:bg-bg-faint tiny-md"
      >
        <CircleHelp className="w-5 h-5" />
        <span>{sidebarLabels["help"]}</span>
      </Link>
      <Link
        href={path.PREFERENCES}
        className="text-fg-base flex gap-3 items-center h-9 px-2 mx-1 rounded-lg hover:bg-bg-faint tiny-md"
      >
        <Settings className="w-5 h-5" />
        <span>{userPopoverLabels["settings"]}</span>
      </Link>

      {isSignedIn && userCanViewApiLink && (
        <Link
          href={apiManagementPageUrl()}
          className="text-fg-base flex gap-3 items-center h-9 px-2 mx-1 rounded-lg hover:bg-bg-faint tiny-md"
        >
          <Code className="w-5 h-5" />
          <span>{userPopoverLabels["api-dashboard"]}</span>
        </Link>
      )}
      <div className="flex items-center rounded-lg mx-1 hover:bg-bg-faint text-alert-fg-base">
        <button
          className="flex gap-3 items-center w-full tiny-md h-9 px-2"
          onClick={handleSignOut}
        >
          <ArrowRightFromLine className="w-5 h-5" />
          <span>{userPopoverLabels["sign-out"]}</span>
        </button>
      </div>
      <footer className="flex justify-between items-center px-3 py-2 mt-1 border-t border-t-border-base">
        <div className="flex gap-3 items-center text-fg-muted tiny-normal">
          <a href={ABOUT_URL} target="_blank" className="underline">
            {sidebarLabels["about"]}
          </a>
          <Link
            className="flex items-center"
            href={GPT_URL}
            target="_blank"
            passHref
          >
            <span>
              <i className="text-base icon-gpt" />
            </span>
            <span className="underline mr-2 ml-1.5">
              {sidebarLabels["gpt"]}
            </span>
            <i className="icon-external-link text-fg-muted" />
          </Link>
        </div>
        <div className="flex gap-3">
          <Link
            className="flex justify-center items-center"
            href={TWITTER_URL}
            target="_blank"
            passHref
          >
            <i className="text-base text-fg-muted icon-x-social" />
          </Link>
          <Link
            className="flex justify-center items-center"
            href={SLACK_URL}
            target="_blank"
            passHref
          >
            <i className="text-base text-fg-muted icon-slack" />
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default UserMenuContents;
